/**
 * EL TIPO EN TARJETAS (MAD-IMPLEMENTAR-DISENO-T1, propuesta A que eligio Josse; Ares #42796). Sin DOM, para el check.
 *
 * Josse: "si yo quisiera cambiar el tipo de cubierta por otra para cambiar el valor, se podria?". Si: el tipo se elige
 * en tarjetas (nombre, precio por unidad y una muestra de color), el area se escribe UNA vez y la partida del tipo se
 * REEMPLAZA al cambiar de tarjeta, asi el subtotal y el total cambian en el acto.
 *
 * Lo que sigue igual del arbol de Atenea (seleccionCubierta.js): los tipos salen de la tabla curada por id del catalogo;
 * nada entra sin una cantidad escrita (aqui, el area); un tipo con varias variantes servidas (los compuestos) se sigue
 * eligiendo partida por partida con "Agregar". Lo que cambia: con UNA variante servida, elegir la tarjeta con el area
 * escrita pone esa partida y cambiar de tarjeta la reemplaza (antes quedaba la vieja y un aviso de revisar).
 *
 * @module application/presupuestos/tipoEnTarjetas
 */
import { TIPOS } from './arbolCubierta.js';
import { candidatosDe, cantidadExplicita } from './seleccionCubierta.js';

/**
 * Una tarjeta por tipo del arbol, con lo que la plantilla servida trae de el.
 * unica: la variante servida si hay EXACTAMENTE una (se pone sola al elegir); si hay varias, null (se elige en la lista).
 * precio y unidad: los de la unica; si hay varias, el menor precio como "desde".
 */
export function tarjetasDeTipos(plantilla) {
  return TIPOS.map((t) => {
    const { candidatos } = candidatosDe(plantilla, t.codigo);
    const precios = candidatos.map((c) => Number(c.item.precioRef)).filter((p) => Number.isFinite(p) && p > 0);
    const unica = candidatos.length === 1 ? candidatos[0] : null;
    return {
      codigo: t.codigo,
      nombre: t.nombre,
      servidas: candidatos.length,
      unica,
      unidad: unica ? unica.item.unidad : (candidatos[0]?.item.unidad ?? null),
      precio: unica ? Number(unica.item.precioRef) || null : null,
      desde: !unica && precios.length ? Math.min(...precios) : null
    };
  });
}

/** El valor de una linea como lo cuenta calcularAiu: cantidad x precio, redondeado a peso. */
export const valorDeLinea = (cantidad, precio) => Math.round((Number(cantidad) || 0) * (Number(precio) || 0));

/**
 * La partida del tipo dentro de una seccion del armador, o null. Se reconoce por la marca tipoCubierta (el codigo del
 * tipo) que le pone ponerPartidaDelTipo; el guardado arma el cuerpo campo por campo, asi que la marca no viaja a la API.
 */
export function partidaDelTipo(seccion) {
  return (seccion?.items || []).find((it) => it && it.tipoCubierta) || null;
}

/**
 * Pone la partida del tipo en la seccion (mutando seccion.items, como el resto del armador): quita la partida de tipo
 * que hubiera y agrega `item` con la cantidad = area escrita y la marca del tipo. Devuelve { puesta, quitada } o
 * { error } si el area no es un numero positivo escrito (no se pone nada).
 */
export function ponerPartidaDelTipo(seccion, item, codigo, areaTexto) {
  const area = cantidadExplicita(areaTexto);
  if (area === null) return { error: 'Escribe el area de la cubierta (mayor que cero) para ver el valor.' };
  const quitada = partidaDelTipo(seccion);
  seccion.items = (seccion.items || []).filter((it) => it !== quitada);
  const puesta = { ...item, cantidad: area, tipoCubierta: codigo };
  seccion.items.push(puesta);
  return { puesta, quitada };
}

/**
 * Quita la partida del tipo (si hay) y la devuelve, o null. Se usa al pasar a un tipo que no pone partida sola (varias
 * variantes, "Todavia no definido") o sin area valida: la cubierta del tipo anterior no puede quedar sumando junto a la
 * nueva (Q #42852).
 */
export function quitarPartidaDelTipo(seccion) {
  const p = partidaDelTipo(seccion);
  if (p) seccion.items = seccion.items.filter((it) => it !== p);
  return p;
}

/** Al cambiar el area: la partida del tipo toma la nueva cantidad (si hay partida y el area es valida). */
export function cambiarAreaDelTipo(seccion, areaTexto) {
  const area = cantidadExplicita(areaTexto);
  const p = partidaDelTipo(seccion);
  if (area === null || !p) return false;
  p.cantidad = area;
  return true;
}

/**
 * La muestra visual de cada tipo: un nombre de material para pintar un bloque de color por CSS
 * ([data-material] en artifacts.css). Sin fotos inventadas: las reales las prepara Atlas despues sin cambiar esto.
 */
export const MATERIAL = {
  barro: 'Barro',
  'shingle-roja-verde': 'Shingle roja o verde',
  'shingle-negra': 'Shingle negra',
  techoline: 'Techoline',
  'plana-sigma': 'Sigma tabaco',
  eternit: 'Fibrocemento',
  manto: 'Manto asfaltico',
  upvc: 'UPVC gris',
  monoroof: 'Panel termoacustico',
  plano: 'Impermeabilizado'
};
